import { STATE } from 'app/constants';
import { ActionDispatching } from 'app/types-shared';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { complexValidationFromServer } from 'app/infrastructure/data/validation';
import { ErrorNotification, SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { createDescriptionSelector } from 'app/infrastructure/translate';
import { isForbiddenStatus, blockAccess } from 'app/area/shared/actions-utils';
import { api as codeSelectorApi } from 'app/area/code-selector/api';
import { FilterSearchModel } from 'app/area/search-filters/types-store';
import { mapCodeItemAccess } from '../item/mapping/map-code-item';
import { api, codetableOptionsActionCreators } from '../common';
import { codeFieldIncludeInactive } from '../common/codetable-options-handler/constants';
import { CodetableTranslation, indicatorFactorsAdministrationTranslation as text } from '../constants';
import { CodeFieldIncludeInactiveKey, CodeFieldWireKey, CodeFieldFlagWireKey } from '../types-store';
import { IndicatorFactorsPicker } from '../types';
import { mapDuplicateRowsToWire } from './mapping/map-duplicate-rows-to-wire';
import { mapListToStore } from './mapping/map-list-to-store';
import {
  UpdateQuery, FailCodetableAdministrationList,
  RequestCodetableAdministrationList, ReceiveCodetableAdministrationList,
  RequestCodeItemDelete, ReceiveCodeItemDelete, FailCodeItemDelete,
  UpdateIncludeInactive, UpdateSearchModel, ResetListState,
  DuplicateRowsFieldsByKey, RequestCodeItems, ReceiveCodeItems, FailCodeItems, RequestDuplicateRows, ReceiveDuplicateRows,
  FailDuplicateRows, UpdateDuplicateRowsValidation, ClearDuplicateRows, ResetValidation,
  ModifyItem, CancelChanges, RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges, RequestLoadCodeParameters,
  ReceiveLoadCodeParameters, FailLoadCodeParameters, UpdateIsDirty, RequestDeleteRows, ReceiveDeleteRows, FailDeleteRows
} from './action-types';
import { CodeListModifiedItem, LoadCodeParametersPayload, LoadCodeItemsPayload, CodetableListState } from './types-store';
import { defaultIncludeInactiveCodes, defaultMaxResults } from './constants';

export const actionCreators = {

  ...codetableOptionsActionCreators,

  updateSearchModel: (model: FilterSearchModel): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateSearchModel(model));
    },

  loadCodetableAdministrationList: (): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCodetableAdministrationList());

      try {
        const codetables = await codeSelectorApi.codetableAdministrationList();

        const codeItems = codetables.rootCodes ? codetables.rootCodes.map(codeItemToStoreValue) : [];

        dispatch(new ReceiveCodetableAdministrationList(codeItems));
      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.indicatorFactors,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodetableAdministrationList());
      }
    },

  resetListState: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {
      dispatch(new ResetListState());
      dispatch(new UpdateIncludeInactive(CodeFieldIncludeInactiveKey.active));
    },

  loadCodeItems: (payload: LoadCodeItemsPayload):
    ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      const { codetableId, parentId, categoryId,
        startIndex = 0,
        maxResults = defaultMaxResults,
        includeInactiveCodes = codeFieldIncludeInactive[defaultIncludeInactiveCodes],
        ignoreCategoryFilters, searchModel = { query: { text: '' } },
        isCentralAdmin } = payload;

      const requestId = Date.now();
      dispatch(new RequestCodeItems(codetableId, requestId, searchModel, startIndex === 0));

      try {
        const codeItemsResponse = await api.loadCodeItems({
          id: codetableId,
          parentId,
          categoryId,
          model: searchModel,
          startIndex,
          maxResults,
          displayNotSelected: true,
          limitFieldsTo: [
            CodeFieldWireKey.code,
            CodeFieldFlagWireKey.substance,
            CodeFieldFlagWireKey.indicatorSubstance,
            CodeFieldFlagWireKey.substanceCategory,
            CodeFieldFlagWireKey.indicatorSubstanceCategory,
            CodeFieldFlagWireKey.substanceUom,
            CodeFieldFlagWireKey.indicatorType,
            CodeFieldFlagWireKey.factor,
            CodeFieldFlagWireKey.validFrom,
            CodeFieldFlagWireKey.indicatorSubstanceUom,
            CodeFieldFlagWireKey.location,
            CodeFieldFlagWireKey.unit,
            CodeFieldFlagWireKey.source,
            CodeFieldFlagWireKey.equipment
          ],
          includeInactiveCodes,
          ignoreCategoryFilters
        });

        const codeItems = mapListToStore(codeItemsResponse.results);

        const numberOfResults = codeItemsResponse.totalNumberOfResults;

        const codeIds = codeItems.map(codeItem => codeItem.id);
        const codeAccesses = isCentralAdmin
          ? []
          : await api.loadCodesAccesses(codetableId, codeIds);
        const mappedCodesAccess = codeIds.map(codeId => mapCodeItemAccess(codeId, codeAccesses));

        dispatch(new ReceiveCodeItems(codetableId, requestId, codeItems, numberOfResults, mappedCodesAccess));

      } catch (err) {
        if (isForbiddenStatus(err)) {
          blockAccess({
            dispatch,
            getState,
            area: STATE.indicatorFactors,
            translationId: text.no_access_to_codetable_administration
          });
        } else if (err && (err.userMessages || err.message)) {
          const error = err && (err.userMessages || err.message);
          error && dispatch(new ErrorNotification(error));
        }

        dispatch(new FailCodeItems(codetableId, requestId));

      }
    },

  deleteCodeItem: (codetableId: number, codeId: number, successMessage: string): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new RequestCodeItemDelete(codetableId, codeId));
      try {
        await api.deleteCodeItem(codetableId, codeId);

        dispatch(new ReceiveCodeItemDelete(codeId));
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
        dispatch(new FailCodeItemDelete(codeId));
      }
    },

  duplicateRows: (onSuccess: () => void, onError: () => void): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      const state = getState()?.indicatorFactors;
      const duplicateRowsForm = state?.list?.duplicateRows?.form;
      const requestedSearchModel = state?.list?.requestedSearchModel;
      const mapped = mapDuplicateRowsToWire(duplicateRowsForm, requestedSearchModel);
      const translationDescription = createDescriptionSelector(state?.texts);

      dispatch(new RequestDuplicateRows());

      try {
        await api.duplicateRows(mapped);

        dispatch(new ReceiveDuplicateRows());
        dispatch(new SuccessNotification(
          replaceTemplateString(translationDescription(text.duplicate_rows_success),
            state?.list?.numberOfResults)
        ));

        onSuccess();
      } catch (err) {

        dispatch(new FailDuplicateRows());

        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));

        if (!!err && !!err.validationErrors) {
          dispatch(new UpdateDuplicateRowsValidation(complexValidationFromServer(err.validationErrors)));
          onError();
        }
      }
    },

  deleteRows: (
    codetableId: number,
    list: CodetableListState,
    translation: CodetableTranslation,
    onSuccess: () => void
  ): ActionDispatching<any, any> =>
    async (dispatch: any) => {
      dispatch(new RequestDeleteRows());

      try {
        await api.deleteRows(codetableId, list?.requestedSearchModel);
        dispatch(new ReceiveDeleteRows());
        dispatch(new SuccessNotification(
          replaceTemplateString(translation.delete_rows_success,
            list?.numberOfResults)
        ));

        onSuccess();
      } catch (err) {
        dispatch(new FailDeleteRows());
        const error = err && (err.userMessages || err.message);
        error && dispatch(new ErrorNotification(error));
      }
    },

  loadCodeParameters: (payload: LoadCodeParametersPayload):
    ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      dispatch(new RequestLoadCodeParameters());

      try {
        const codeItemsResponse = await api.loadCodeItems({
          id: payload.codetableId,
          code: { text: payload.text }
        });

        const codeItems = mapListToStore(codeItemsResponse.results);

        dispatch(new ReceiveLoadCodeParameters(codeItems));

      } catch (err) {
        dispatch(new FailLoadCodeParameters());
      }
    },

  clearDuplicateRows: (): ActionDispatching<any, any> =>
    async (dispatch, getState) => {

      dispatch(new ClearDuplicateRows());
    },

  updateQuery: (value: string): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateQuery(value));
    },

  updateIncludeInactive: (value: CodeFieldIncludeInactiveKey): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new UpdateIncludeInactive(value));
    },

  updateByKey: <T>(key: IndicatorFactorsPicker, value: T): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new DuplicateRowsFieldsByKey(key, value));
  },

  resetValidation: (key: string): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new ResetValidation(key));
  },

  updateIsDirty: (value: boolean): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
    dispatch(new UpdateIsDirty(value));
  },

  modifyItem: (modifiedItem: CodeListModifiedItem): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new ModifyItem(modifiedItem));
    },

  cancelChanges: (): ActionDispatching<any, any> =>
    (dispatch: any, getState: any) => {

      dispatch(new CancelChanges());
    },

  saveChanges: (
    modifiedItems: CodeListModifiedItem[],
    successMessage: string,
    validationErrorMessage: string
  ): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {

      try {
        dispatch(new RequestSaveChanges());

        await api.saveChanges(modifiedItems);

        dispatch(new ReceiveSaveChanges());
        dispatch(new SuccessNotification(successMessage));
      } catch (err) {
        const validationError = Object.keys(err?.validationErrors ?? {}).length > 0
          ? complexValidationFromServer(err.validationErrors)
          : undefined;
        dispatch(new FailSaveChanges(validationError));

        if (validationError) {
          dispatch(new ErrorNotification(validationErrorMessage));
        }

        if (err?.userMessages?.length ?? 0 > 0) {
          dispatch(new ErrorNotification(err.userMessages));
        }
      }
    }
};
